import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Flag } from "lucide-react";
import { toast } from "sonner";

export default function Pipelines() {
  const [items, setItems] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [candTypes, setCandTypes] = useState([]);
  const [editing, setEditing] = useState(null);   // pipeline being edited, or {} for new
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s, c] = await Promise.all([api.get("/pipelines"), api.get("/statuses"), api.get("/candidate-types")]);
      setItems(p.data.items); setStatuses(s.data.items); setCandTypes(c.data.items);
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!items) return <PageLoader />;

  const statusLabel = (k) => statuses.find((s) => s.key === k)?.label || (k || "—");

  const toggleActive = async (p) => {
    try { await api.patch(`/pipelines/${p.id}`, { active: !(p.active !== false) }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const mapCandidateType = async (ct, pipelineId) => {
    try { await api.patch(`/candidate-types/${ct.id}`, { default_pipeline_id: pipelineId }); toast.success("Default pipeline updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const save = async (form) => {
    setSaving(true);
    try {
      const payload = { name: form.name, active: form.active,
        stages: form.stages.map((s, i) => ({ id: s.id, name: s.name, default_status: s.default_status || null, order: i })) };
      if (form.id) await api.patch(`/pipelines/${form.id}`, payload);
      else await api.post("/pipelines", payload);
      toast.success(form.id ? "Pipeline updated" : "Pipeline created"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Recruiting Pipelines" description="Each Candidate Type can use a different pipeline. Stages belong to the pipeline; Opportunity Status is universal."
        actions={<Button className="bg-brand hover:bg-brand-strong" data-testid="add-pipeline" onClick={() => setEditing({ name: "", active: true, stages: [{ id: null, name: "Lead", default_status: "active" }] })}><Plus className="w-4 h-4 mr-1.5" /> New Pipeline</Button>} />

      <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-600 mb-5 flex items-center gap-2">
        <Flag className="w-4 h-4 text-brand" /> Universal Opportunity Statuses are managed under <Link to="/admin/statuses-sources" className="text-brand font-medium hover:underline" data-testid="link-statuses">Statuses &amp; Sources</Link>.
      </div>

      <div className="space-y-4">
        {items.map((p) => (
          <div key={p.id} className={`rounded-lg border bg-white p-5 shadow-sm ${p.active === false ? "border-slate-200 opacity-70" : "border-slate-200"}`} data-testid={`pipeline-${p.id}`}>
            <div className="flex items-center justify-between mb-3 gap-3">
              <div><h3 className="font-display font-semibold text-slate-900">{p.name}
                {p.is_default && <span className="ml-2 text-xs text-brand">(default)</span>}
                {p.active === false && <span className="ml-2 text-xs text-slate-400">(inactive)</span>}</h3></div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5"><span className="text-xs text-slate-500">Active</span>
                  <Switch checked={p.active !== false} onCheckedChange={() => toggleActive(p)} data-testid={`pipeline-active-${p.id}`} /></div>
                <Button size="sm" variant="outline" data-testid={`pipeline-edit-${p.id}`}
                  onClick={() => setEditing({ id: p.id, name: p.name, active: p.active !== false,
                    stages: p.stages.slice().sort((a, b) => a.order - b.order).map((s) => ({ id: s.id, name: s.name, default_status: s.default_status || "" })) })}>
                  <Pencil className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {p.stages.slice().sort((a, b) => a.order - b.order).map((s, i) => (
                <React.Fragment key={s.id}>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm">
                    <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />{s.name}
                    <span className="text-[10px] text-slate-400 uppercase tracking-wide">→ {statusLabel(s.default_status)}</span></span>
                  {i < p.stages.length - 1 && <span className="text-slate-300">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Candidate Type -> Default Pipeline mapping */}
      <div className="mt-8 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="candidate-type-mapping">
        <h3 className="font-display font-semibold text-slate-900 mb-1">Candidate Type → Default Pipeline</h3>
        <p className="text-sm text-slate-500 mb-4">New opportunities of each Candidate Type start in its default pipeline.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {candTypes.filter((c) => c.active !== false).map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 px-3 py-2" data-testid={`ct-map-${c.key}`}>
              <span className="text-sm font-medium text-slate-700">{c.label}</span>
              <Select value={c.default_pipeline_id || "__none__"} onValueChange={(v) => mapCandidateType(c, v === "__none__" ? null : v)}>
                <SelectTrigger className="w-52 h-9" data-testid={`ct-map-select-${c.key}`}><SelectValue placeholder="Account default" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="__none__">Account default</SelectItem>
                  {items.filter((p) => p.active !== false).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      <LeadDefaultCard pipelines={items.filter((p) => p.active !== false)} />

      {editing && <PipelineEditor initial={editing} statuses={statuses} saving={saving} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function LeadDefaultCard({ pipelines }) {
  const [val, setVal] = useState(null);
  const [review, setReview] = useState(null);
  useEffect(() => {
    api.get("/recruiting-settings/lead-default").then((r) => setVal(r.data.pipeline_id || "__none__")).catch(() => setVal("__none__"));
    api.get("/recruiting-settings/defaulted-leads").then((r) => setReview(r.data)).catch(() => setReview(null));
  }, []);
  const change = async (v) => {
    try {
      const { data } = await api.put("/recruiting-settings/lead-default", { pipeline_id: v === "__none__" ? null : v });
      setVal(data.pipeline_id || "__none__"); toast.success("Default for new leads updated");
    } catch (e) { toast.error(apiError(e)); }
  };
  if (val === null) return null;
  return (
    <div className="mt-8 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="lead-default-card">
      <h3 className="font-display font-semibold text-slate-900 mb-1">New Leads Without a Program</h3>
      <p className="text-sm text-slate-500 mb-4">Leads added without a selected Candidate Type start here. Unknown keeps them in intake with no pipeline or program requirements until someone chooses the path.</p>
      <Select value={val} onValueChange={change}>
        <SelectTrigger className="w-64 h-9" data-testid="lead-default-select"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">
          <SelectItem value="__none__">Unknown (no pipeline)</SelectItem>
          {pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {review && (
        <div className="mt-5 border-t border-slate-100 pt-4" data-testid="defaulted-leads-review">
          <p className="text-sm font-medium text-slate-700">Possibly auto-placed in {review.pipeline_name || "the default pipeline"}: <span data-testid="defaulted-leads-count">{review.items.length}</span></p>
          <p className="text-xs text-slate-400 mb-2">No Candidate Type, never moved between pipelines, no program enrollment. Review only — nothing is changed automatically.</p>
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {review.items.map((r) => (
              <Link key={r.id} to={`/recruiting/opportunity/${r.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm hover:bg-slate-50 px-1" data-testid={`defaulted-lead-${r.id}`}>
                <span className="text-slate-800">{r.person_name} <span className="text-slate-400 font-mono text-xs">{r.human_id}</span></span>
                <span className="text-xs text-slate-500">{r.organization_name || "—"} · {r.stage}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PipelineEditor({ initial, statuses, saving, onSave, onClose }) {
  const [form, setForm] = useState(initial);
  const setStage = (i, patch) => setForm((f) => ({ ...f, stages: f.stages.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const addStage = () => setForm((f) => ({ ...f, stages: [...f.stages, { id: null, name: "", default_status: "active" }] }));
  const removeStage = (i) => setForm((f) => ({ ...f, stages: f.stages.filter((_, idx) => idx !== i) }));
  const move = (i, dir) => setForm((f) => {
    const arr = f.stages.slice(); const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...f, stages: arr };
  });

  const valid = form.name.trim() && form.stages.length && form.stages.every((s) => s.name.trim());

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto tr-scroll" data-testid="pipeline-editor-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Edit Pipeline" : "New Pipeline"}</DialogTitle>
          <DialogDescription>Order the stages and set each stage's suggested Opportunity Status (overridable by recruiters).</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">Pipeline name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="pipeline-name" /></div>
            <div className="flex items-end gap-2 pb-1"><span className="text-sm text-slate-600">Active</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pipeline-active-toggle" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-slate-700">Stages (in order)</Label>
              <Button size="sm" variant="outline" onClick={addStage} data-testid="add-stage"><Plus className="w-4 h-4 mr-1" /> Add stage</Button>
            </div>
            <div className="space-y-2" data-testid="stage-editor">
              {form.stages.map((s, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`stage-row-${i}`}>
                  <span className="text-xs text-slate-400 w-5 text-right">{i + 1}</span>
                  <Input value={s.name} onChange={(e) => setStage(i, { name: e.target.value })} placeholder="Stage name" className="flex-1" data-testid={`stage-name-${i}`} />
                  <Select value={s.default_status || "__none__"} onValueChange={(v) => setStage(i, { default_status: v === "__none__" ? "" : v })}>
                    <SelectTrigger className="w-44 h-9" data-testid={`stage-status-${i}`}><SelectValue placeholder="Default status" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-60">
                      <SelectItem value="__none__">No default</SelectItem>
                      {statuses.map((st) => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-700 p-1" data-testid={`stage-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(i, 1)} className="text-slate-400 hover:text-slate-700 p-1" data-testid={`stage-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => removeStage(i)} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`stage-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={saving || !valid} data-testid="pipeline-submit" className="bg-brand hover:bg-brand-strong">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
